/**
 * Admin Deals — per-region snapshots of seats.aero `/_api/v1/deals`.
 *
 * seats.aero's consumer API is Cloudflare-protected and has no CORS, so we
 * can't fetch it from the server or the browser. Each region card shows the
 * pre-built URL; the admin opens it, copies the JSON, pastes it into the
 * region's textarea, and we save a normalized snapshot.
 */

import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Facebook, Instagram, Linkedin, RefreshCw, Ticket, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

// Lucide doesn't ship a TikTok glyph yet, so we inline the official mark.
function TikTokIcon({ size = 14 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M16.5 3a5.5 5.5 0 0 0 4.5 4.5v3a8.5 8.5 0 0 1-4.5-1.4v6.4a6.5 6.5 0 1 1-6.5-6.5c.3 0 .6 0 .9.1v3.2a3.3 3.3 0 1 0 2.3 3.2V3h3.3z" />
    </svg>
  );
}

// Lucide doesn't ship a Reddit glyph either — inline the alien-head mark.
function RedditIcon({ size = 14 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M22 12.5c0-1.1-.9-2-2-2-.5 0-1 .2-1.4.6-1.5-1-3.4-1.6-5.5-1.7l1-3.5 3 .7c0 .8.7 1.4 1.5 1.4S20 7.3 20 6.5 19.3 5 18.5 5c-.6 0-1.1.4-1.4.9l-3.4-.8c-.2 0-.4.1-.5.3l-1.2 4c-2.2 0-4.2.7-5.7 1.7-.4-.4-.9-.6-1.4-.6-1.1 0-2 .9-2 2 0 .7.4 1.4 1.1 1.7-.1.3-.1.6-.1.9 0 2.9 3.4 5.3 7.5 5.3s7.5-2.4 7.5-5.3c0-.3 0-.6-.1-.9.7-.3 1.2-1 1.2-1.7zM7.4 14c0-.7.6-1.3 1.3-1.3.7 0 1.3.6 1.3 1.3 0 .7-.6 1.3-1.3 1.3-.7 0-1.3-.6-1.3-1.3zm7.6 3.6c-.8.8-2.3 1-3 1s-2.2-.2-3-1c-.1-.1-.1-.3 0-.4.1-.1.3-.1.4 0 .5.5 1.6.7 2.6.7s2.1-.2 2.6-.7c.1-.1.3-.1.4 0 .1.1.1.3 0 .4zm-.3-2.3c-.7 0-1.3-.6-1.3-1.3 0-.7.6-1.3 1.3-1.3.7 0 1.3.6 1.3 1.3 0 .7-.6 1.3-1.3 1.3z" />
    </svg>
  );
}

// --- formatting helpers (small, self-contained — see FlightResults for the
// fuller versions; keeping these inline to avoid coupling) -----------------

function fmtDuration(mins) {
  if (!mins || typeof mins !== "number") return "";
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtTaxes(amount, currency) {
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) return "";
  const sym = { USD: "$", CAD: "C$", AUD: "A$", HKD: "HK$", BRL: "R$", EUR: "€", GBP: "£", DKK: "kr ", SEK: "kr " }[currency] || "";
  const n = Number.isInteger(amount)
    ? amount.toLocaleString()
    : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sym}${n}${sym ? "" : (currency ? " " + currency : "")}`;
}

// seats.aero labels timestamps "Z" but values are local airport wall-clock.
function localTime(iso) {
  const m = String(iso || "").match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}
function localDate(iso) {
  const d = String(iso || "").slice(0, 10);
  if (!d) return "";
  const dt = new Date(`${d}T00:00:00Z`);
  return Number.isNaN(dt.getTime()) ? d
    : dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}
function dayOffset(dep, arr) {
  const d = String(dep || "").slice(0, 10), a = String(arr || "").slice(0, 10);
  if (!d || !a || d === a) return 0;
  const ms = Date.parse(`${a}T00:00:00Z`) - Date.parse(`${d}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.round(ms / 86400000) : 0;
}
function fmtImportedAt(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? iso : dt.toLocaleString();
}

// Compact relative time for the trips table — "12h ago", "3d ago".
// Uses the same iso parser fmtImportedAt does, so a falsy/invalid value
// renders "—".
function relativeAge(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const secs = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (secs < 60)              return "just now";
  if (secs < 3600)            return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400)           return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86400 * 30)      return `${Math.floor(secs / 86400)}d ago`;
  return `${Math.floor(secs / (86400 * 30))}mo ago`;
}

// Points per hour — a rough "good-deal" score (lower = better).
function pph(miles, durMin) {
  if (!miles || !durMin) return null;
  return Math.round((miles / durMin) * 60);
}

// Airport labels are resolved server-side from master_airports and stamped
// onto each trip as `origin_city` / `destination_city`. Frontend renders
// "CDG (Paris)" from that pair, or falls back to bare IATA when missing.
function airportLabel(iata, city) {
  if (!iata) return "";
  const code = String(iata).toUpperCase();
  return city ? `${code} (${city})` : code;
}

// Normalize seats.aero's `carriers` ("UA, UA, LH") → "UA / LH".
function carrierLabel(raw) {
  if (!raw) return "";
  const seen = [];
  for (const part of String(raw).split(",")) {
    const p = part.trim();
    if (p && !seen.includes(p)) seen.push(p);
  }
  return seen.join(" / ");
}

// IATA 2-letter code → friendly airline name. Covers the operating carriers
// we actually see in seats.aero deals (collected from real Asia/EU/NA/SA
// snapshots). Unknown codes pass through as-is so the column still says
// something useful.
const CARRIER_NAMES = {
  AA: "American Airlines",  AC: "Air Canada",       AF: "Air France",
  AI: "Air India",          AM: "Aeromexico",       AS: "Alaska Airlines",
  AT: "Royal Air Maroc",    AV: "Avianca",          B6: "JetBlue",
  BA: "British Airways",    BR: "EVA Air",          CA: "Air China",
  CI: "China Airlines",     CM: "Copa Airlines",    CX: "Cathay Pacific",
  DE: "Condor",             DL: "Delta",            EI: "Aer Lingus",
  EK: "Emirates",           EY: "Etihad",           FJ: "Fiji Airways",
  GA: "Garuda Indonesia",   IB: "Iberia",           JL: "Japan Airlines",
  JQ: "Jetstar",            JX: "Starlux",          KE: "Korean Air",
  KL: "KLM",                LA: "LATAM",            LH: "Lufthansa",
  LO: "LOT Polish",         LX: "Swiss",            MU: "China Eastern",
  NH: "ANA",                NQ: "Air Japan",        OS: "Austrian",
  OZ: "Asiana",             PR: "Philippine Airlines", QR: "Qatar Airways",
  SK: "SAS",                SN: "Brussels Airlines", SQ: "Singapore Airlines",
  TG: "Thai Airways",       TK: "Turkish Airlines",  TN: "Air Tahiti Nui",
  UA: "United",             UL: "SriLankan",         VN: "Vietnam Airlines",
  VS: "Virgin Atlantic",    WY: "Oman Air",          YU: "EuroAtlantic",
  ZH: "Shenzhen Airlines",
};

function carrierNames(raw) {
  if (!raw) return "";
  const out = [];
  for (const part of String(raw).split(",")) {
    const code = part.trim().toUpperCase();
    if (!code || out.find((x) => x.code === code)) continue;
    out.push({ code, name: CARRIER_NAMES[code] || code });
  }
  if (!out.length) return "";
  return out.map((x) => x.name).join(" / ");
}

// One-sentence human-readable summary of a deal — shown inline on the
// admin page and reused as the body of every share caption so the page
// preview matches what gets posted.
function cashValueClause(trip) {
  const cash = trip.cash_price;
  if (typeof cash !== "number" || !Number.isFinite(cash) || cash <= 0) return "";
  const cur = trip.cash_currency || "USD";
  const sym = cur === "USD" ? "$" : "";
  const cashStr = sym ? `${sym}${Math.round(cash).toLocaleString()}` : `${Math.round(cash).toLocaleString()} ${cur}`;
  const bits = [`Worth ≈${cashStr} cash`];
  if (typeof trip.cpp === "number" && trip.cpp > 0) bits.push(`${trip.cpp.toFixed(1)}¢/pt`);
  return bits.join(" — ") + ".";
}

function dealDescription(trip) {
  const route = `${airportLabel(trip.origin, trip.origin_city)} → ${airportLabel(trip.destination, trip.destination_city)}`;
  const date = localDate(trip.departs_at);
  const dep = localTime(trip.departs_at);
  const arr = localTime(trip.arrives_at);
  const offs = dayOffset(trip.departs_at, trip.arrives_at);
  const timeWindow = dep && arr ? `${dep}→${arr}${offs > 0 ? ` +${offs}` : ""}` : "";
  const miles = trip.mileage_cost ? `${trip.mileage_cost.toLocaleString()} miles` : "";
  const taxes = fmtTaxes(trip.taxes_amount, trip.taxes_currency);
  const dur = fmtDuration(trip.total_duration_min);
  const stops = trip.stops > 0 ? `${trip.stops} stop${trip.stops === 1 ? "" : "s"}` : "nonstop";
  const cabin = trip.cabin ? trip.cabin[0].toUpperCase() + trip.cabin.slice(1) : "this";
  const carrier = carrierLabel(trip.carriers);
  const on = carrier ? ` on ${carrier}` : "";
  const fn = trip.flight_numbers ? ` (${trip.flight_numbers})` : "";
  const cost = [miles, taxes].filter(Boolean).join(" + ");
  const seats = typeof trip.remaining_seats === "number" && trip.remaining_seats > 0
    ? ` ${trip.remaining_seats > 8 ? "9+" : trip.remaining_seats} award seat${trip.remaining_seats === 1 ? "" : "s"} left.`
    : "";
  const value = cashValueClause(trip);
  const valueSuffix = value ? ` ${value}` : "";
  const head = `Book ${cabin} class ${route} on ${date}${timeWindow ? ` (${timeWindow})` : ""}${cost ? ` for ${cost}` : ""}.`;
  const tail = `${dur} ${stops}${on}${fn}.`;
  return `${head} ${tail}${seats}${valueSuffix}`.trim();
}

// One-line title for Reddit/Twitter-style share targets that take a separate
// title field. Kept in sync with backend share_routes.py `_title()`.
function dealTitle(trip) {
  const route = `${airportLabel(trip.origin, trip.origin_city)} → ${airportLabel(trip.destination, trip.destination_city)}`;
  const cabin = trip.cabin ? trip.cabin[0].toUpperCase() + trip.cabin.slice(1) : "";
  const date = localDate(trip.departs_at);
  const bits = [route, cabin, date].filter(Boolean);
  return "DEAL ALERT!!! · " + bits.join(" · ");
}

// Caption used by every share target. Plain-text — Instagram/TikTok captions
// don't render markdown, and FB/LinkedIn web-share strips formatting anyway.
function shareCaption(trip) {
  return [
    "DEAL ALERT!!!",
    dealDescription(trip),
    "",
    "Find more award deals at myreservoir.ai",
  ].join("\n");
}

// Build the per-deal share URL. Points at our public /share/deal/... page,
// which returns OpenGraph meta tags so FB/LinkedIn/Slack render a card with
// the deal's title + description (their share dialogs ignore `quote`/`summary`
// params, so OG tags on the URL are the only way to get text in the post).
// Lives under /api/ so production's nginx (which only forwards /api/* to
// FastAPI) routes the request to the backend instead of serving the SPA.
function dealShareUrl(trip) {
  const base = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://myreservoir.ai";
  if (!trip?.id) return base;
  return `${base}/api/share/deal/${encodeURIComponent(trip.id)}`;
}

async function copyCaption(caption) {
  // navigator.clipboard requires document focus. If the user has clicked a
  // share button that just opened a popup, focus has moved and this rejects
  // — we silently fail rather than throwing up a window.prompt modal.
  try {
    await navigator.clipboard.writeText(caption);
    return true;
  } catch {
    return false;
  }
}

// Lazy "Book" button. First click POSTs /booking-link, which calls seats.aero
// /trips/{id} server-side, persists the URL on the trip, and returns it; we
// then open it in a new tab. Subsequent clicks for the same trip skip the API
// call entirely and open the cached URL directly.
function BookButton({ trip: tripProp }) {
  const [trip, setTrip] = useState(tripProp);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const openLink = (url) => {
    if (!url) return;
    // Anchor click instead of window.open: window.open with `noopener` ALWAYS
    // returns null per spec, which made the old "if (!w) fallback" path
    // wrongly trigger a same-tab redirect on top of the new tab opening.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onClick = async () => {
    if (trip.booking_link) {
      openLink(trip.booking_link);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/admin/deals/trips/${trip.id}/booking-link`);
      const url = data?.booking_link;
      if (!url) throw new Error("no booking link returned");
      setTrip((t) => ({ ...t, booking_link: url, booking_label: data?.booking_label }));
      openLink(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Couldn't get booking link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        title={trip.booking_link ? `Open ${trip.booking_label || "booking page"}` : "Get booking link"}
        className="btn text-[11.5px] py-1 px-2 inline-flex items-center gap-1 disabled:opacity-50"
        data-testid={`book-${trip.id}`}
      >
        {busy ? <RefreshCw size={11} className="animate-spin" /> : <Ticket size={11} />}
        {busy ? "Finding…" : "Book"}
      </button>
      {err && (
        <span className="text-[10.5px] text-red-700" title={err}>!</span>
      )}
    </div>
  );
}

function ShareButtons({ trip: tripProp }) {
  // Keep the share data in local state so a refresh-cash call updates the
  // caption + OG-driving URL for subsequent clicks in the same session.
  const [trip, setTrip] = useState(tripProp);
  const [copied, setCopied] = useState(null); // "facebook" | "linkedin" | "reddit" | "instagram" | "tiktok" | null
  const [busy, setBusy] = useState(null);

  const url = dealShareUrl(trip);

  const flash = (which) => {
    setCopied(which);
    setTimeout(() => setCopied(null), 1800);
  };

  // Refresh cash + CPP from SerpAPI Google Flights before sharing. The
  // endpoint persists the result on the trip in deals_trips, so the
  // OG-tagged share URL (FB/LinkedIn preview) renders with the new value
  // when the platform scrapes it. Returns the merged trip for caption use.
  // Short-circuits if we already refreshed this trip today (UTC).
  const refreshAndMerge = async () => {
    const lastDay = (trip.cash_refreshed_at || "").slice(0, 10);
    const todayUtc = new Date().toISOString().slice(0, 10);
    if (lastDay === todayUtc && trip.cash_price) return trip;
    try {
      const { data } = await api.post(`/admin/deals/trips/${trip.id}/refresh-cash`);
      if (data?.ok) {
        const merged = { ...trip, ...data };
        setTrip(merged);
        return merged;
      }
    } catch { /* swallow — fall back to whatever cash we already have */ }
    return trip;
  };

  // FB/LinkedIn/Reddit ignore pre-filled text but DO scrape OG from our URL.
  // Two ordering constraints:
  //   1. Clipboard write must happen BEFORE the popup is opened — once focus
  //      moves to the popup, navigator.clipboard rejects.
  //   2. window.open must run synchronously inside the click handler or the
  //      popup blocker fires. We can't await the cash refresh first.
  // So: copy the current trip's caption, open about:blank, then refresh
  // cash in the background and redirect the popup once persisted. The
  // caption may miss the very latest cash for the first share, but the OG
  // card (which is what actually renders in the post preview) is fresh
  // because the backend write completes before the popup navigates.
  //
  // Note: `window.open` returns null when `noopener`/`noreferrer` is set,
  // so we omit those here to keep the reference. Cross-origin SOP still
  // prevents the share target from touching our window via `opener`.
  const openWithCopy = (which, hrefFn) => (e) => {
    e.preventDefault();
    copyCaption(shareCaption(trip));  // fire first while document still has focus
    const popup = window.open("about:blank", "_blank");
    setBusy(which);
    (async () => {
      try {
        const merged = await refreshAndMerge();
        flash(which);
        const href = hrefFn(merged);
        if (popup && !popup.closed) popup.location.href = href;
      } finally {
        setBusy(null);
      }
    })();
  };
  // IG / TikTok have no web-share intent, so just refresh + copy.
  const copyOnly = (which) => async () => {
    setBusy(which);
    try {
      const merged = await refreshAndMerge();
      await copyCaption(shareCaption(merged));
      flash(which);
    } finally {
      setBusy(null);
    }
  };

  const fbHref = (t) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareCaption(t))}`;
  const liHref = (t) =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(shareCaption(t))}`;
  // Reddit's submit page honors both `url` (auto-detects the OG card) and
  // `title` (post headline). Body text isn't a query param, so we still
  // copy the full caption to the clipboard for the optional selftext box.
  const rdHref = (t) =>
    `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(dealTitle(t))}`;
  const btn = "p-1 rounded hover:bg-neutral-100 text-neutral-600 hover:text-black";
  const noteFor = {
    facebook:  "Caption copied — paste it into the Facebook share dialog",
    linkedin:  "Caption copied — paste it into the LinkedIn share dialog",
    reddit:    "Caption copied — paste it into Reddit's post body",
    instagram: "Caption copied — paste it into Instagram",
    tiktok:    "Caption copied — paste it into TikTok",
  };
  return (
    <div className="flex items-center gap-0.5" data-testid="share-buttons">
      <a
        href={fbHref(trip)}
        onClick={openWithCopy("facebook", fbHref)}
        target="_blank"
        rel="noopener noreferrer"
        title="Share on Facebook (refreshes cash + copies caption)"
        className={btn}
      >
        <Facebook size={14} />
      </a>
      <a
        href={liHref(trip)}
        onClick={openWithCopy("linkedin", liHref)}
        target="_blank"
        rel="noopener noreferrer"
        title="Share on LinkedIn (refreshes cash + copies caption)"
        className={btn}
      >
        <Linkedin size={14} />
      </a>
      <a
        href={rdHref(trip)}
        onClick={openWithCopy("reddit", rdHref)}
        target="_blank"
        rel="noopener noreferrer"
        title="Share on Reddit (refreshes cash + copies caption)"
        className={btn}
      >
        <RedditIcon size={14} />
      </a>
      <button
        type="button"
        onClick={copyOnly("instagram")}
        title="Refresh cash + copy caption for Instagram"
        className={btn}
      >
        <Instagram size={14} />
      </button>
      <button
        type="button"
        onClick={copyOnly("tiktok")}
        title="Refresh cash + copy caption for TikTok"
        className={btn}
      >
        <TikTokIcon size={14} />
      </button>
      {busy ? (
        <span className="text-[10.5px] text-neutral-500 ml-1">fetching cash…</span>
      ) : copied ? (
        <span className="text-[10.5px] text-emerald-700 ml-1" title={noteFor[copied]}>
          caption copied
        </span>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------------------

export default function AdminDeals() {
  const [regions, setRegions] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [pasted, setPasted] = useState({});         // {region_key: jsonText}
  const [importing, setImporting] = useState({});   // {region_key: bool}
  const [importErr, setImportErr] = useState({});   // {region_key: msg}
  const { user } = useAuth();
  // Partner-admin role gets read-only access — they need the deals list
  // to share with their audience but should not be importing snapshots
  // or deleting them. Admins keep full control.
  const canManageSnapshots = !!user?.is_admin || (user?.role === "deals_user");
  const [syncOpen, setSyncOpen] = useState(false);  // region paste-import panel
  const [scope, setScope] = useState("international"); // "international" | "domestic"
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [originFilter, setOriginFilter] = useState("");      // IATA or ""
  const [destFilter, setDestFilter] = useState("");          // IATA or ""
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  const [totalTrips, setTotalTrips] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter inputs change fast as the user types — debounce so we don't
  // fire a /trips request per keystroke.
  const [debouncedOrigin, setDebouncedOrigin] = useState("");
  const [debouncedDest, setDebouncedDest] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedOrigin(originFilter.trim()), 250);
    return () => clearTimeout(t);
  }, [originFilter]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedDest(destFilter.trim()), 250);
    return () => clearTimeout(t);
  }, [destFilter]);

  // Any filter/scope change resets pagination to page 1 — otherwise the
  // user can land on a now-empty page after narrowing a filter.
  useEffect(() => { setPage(1); }, [scope, debouncedOrigin, debouncedDest]);

  const load = async () => {
    // Snapshot/region management endpoints 403 for partner_admin —
    // skip the calls entirely instead of hitting them and ignoring
    // the error.
    if (!canManageSnapshots) return;
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/admin/deals/regions"),
        api.get("/admin/deals/snapshots"),
      ]);
      setRegions(r || []);
      setSnapshots(s || []);
    } catch (e) {
      // ignore — endpoints will 403 if not authorized
    }
  };
  useEffect(() => { load(); }, [canManageSnapshots]);

  // Fetch the current page whenever scope/filters/page change. Cancel the
  // in-flight request on rapid changes so stale results don't overwrite
  // newer ones.
  const tripsQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set("scope", scope);
    p.set("page", String(page));
    p.set("page_size", String(PAGE_SIZE));
    if (debouncedOrigin) p.set("origin", debouncedOrigin);
    if (debouncedDest)   p.set("destination", debouncedDest);
    return p.toString();
  }, [scope, page, debouncedOrigin, debouncedDest]);

  useEffect(() => {
    let cancelled = false;
    setTripsLoading(true);
    api.get(`/admin/deals/trips?${tripsQuery}`)
      .then(({ data }) => {
        if (cancelled) return;
        setTrips(data?.items || []);
        setTotalTrips(data?.total || 0);
        setTotalPages(data?.total_pages || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setTrips([]);
        setTotalTrips(0);
        setTotalPages(0);
      })
      .finally(() => { if (!cancelled) setTripsLoading(false); });
    return () => { cancelled = true; };
  }, [tripsQuery]);

  const latestFor = (key) => snapshots.find((s) => s.region_key === key);

  const reloadTrips = () => {
    // Force a re-fetch by bumping a state val? Simpler: call api.get
    // inline since we have tripsQuery cached.
    setTripsLoading(true);
    api.get(`/admin/deals/trips?${tripsQuery}`)
      .then(({ data }) => {
        setTrips(data?.items || []);
        setTotalTrips(data?.total || 0);
        setTotalPages(data?.total_pages || 0);
      })
      .catch(() => {
        setTrips([]);
        setTotalTrips(0);
        setTotalPages(0);
      })
      .finally(() => setTripsLoading(false));
  };

  const importRegion = async (key) => {
    const text = (pasted[key] || "").trim();
    if (!text) return;
    setImporting((p) => ({ ...p, [key]: true }));
    setImportErr((p) => ({ ...p, [key]: null }));
    try {
      await api.post("/admin/deals/import", { region_key: key, pasted_json: text });
      setPasted((p) => ({ ...p, [key]: "" }));
      await load();
      reloadTrips();
    } catch (e) {
      setImportErr((p) => ({
        ...p,
        [key]: e?.response?.data?.detail || String(e?.message || e),
      }));
    } finally {
      setImporting((p) => ({ ...p, [key]: false }));
    }
  };

  // Origin/destination filter runs server-side now (see tripsQuery). The
  // backend matches `iata` OR `city` substring via airport_lookup, so the
  // "lon" → LHR/LGW/STN behavior is preserved across pages.
  const filteredTrips = trips;

  const deleteSnap = async (id) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try {
      await api.delete(`/admin/deals/snapshots/${id}`);
      await load();
      reloadTrips();
    } catch { /* ignore */ }
  };

  return (
    <AdminLayout activeTab="deals" title="Deals">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="text-[13px] text-neutral-600">
          Per-region award-flight deals pulled from seats.aero.
        </div>
        {canManageSnapshots && (
          <button
            type="button"
            onClick={() => setSyncOpen((v) => !v)}
            className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5"
            data-testid="sync-deals-toggle"
          >
            {syncOpen ? <X size={13} /> : <RefreshCw size={13} />}
            {syncOpen ? "Close sync" : "Sync deals"}
          </button>
        )}
      </div>

      {canManageSnapshots && syncOpen && (
      <div className="mb-8">
      <p className="text-[12.5px] text-neutral-600 mb-3">
        seats.aero blocks server-side fetches of <code>/_api/v1/deals</code>.
        For each region, click <b>Open seats.aero ↗</b>, copy the JSON shown in
        the browser, paste it into the textarea below, and save the snapshot.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {regions.map((r) => {
          const last = latestFor(r.key);
          return (
            <section key={r.key} className="card p-4" data-testid={`region-${r.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-bold text-[15px]">{r.label}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5 break-words">
                    Origins: <span className="font-mono">{r.origins.join(", ")}</span>
                  </div>
                  <div className="text-[11.5px] text-neutral-500 mt-2">
                    {last
                      ? <>Last: {fmtImportedAt(last.imported_at)} · <span className="tabular">{last.trip_count}</span> deals</>
                      : "No imports yet."}
                  </div>
                </div>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary text-[12.5px] inline-flex items-center gap-1 shrink-0"
                  data-testid={`open-${r.key}`}
                >
                  Open seats.aero <ExternalLink size={12} />
                </a>
              </div>

              <textarea
                rows={4}
                className="input w-full mt-3 font-mono text-[11px]"
                placeholder="Paste the JSON response from seats.aero here…"
                value={pasted[r.key] || ""}
                onChange={(e) => setPasted((p) => ({ ...p, [r.key]: e.target.value }))}
                disabled={!!importing[r.key]}
              />
              {importErr[r.key] && (
                <div className="text-[12px] text-red-700 mt-1">{importErr[r.key]}</div>
              )}
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => importRegion(r.key)}
                  disabled={!!importing[r.key] || !(pasted[r.key] || "").trim()}
                  className="btn btn-primary text-[12.5px]"
                  data-testid={`save-${r.key}`}
                >
                  {importing[r.key] ? "Saving…" : "Save snapshot"}
                </button>
              </div>
            </section>
          );
        })}
      </div>

      {snapshots.length > 0 && (
        <div className="mt-5">
          <div className="text-[11.5px] uppercase tracking-wide text-neutral-500 font-semibold mb-2">
            Imported snapshots
          </div>
          <div className="overflow-x-auto border border-black/5 rounded-[10px] bg-white">
            <table className="w-full text-[12.5px]">
              <thead className="bg-neutral-50 text-[11px] uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="text-left font-semibold px-3 py-1.5">Region</th>
                  <th className="text-left font-semibold px-3 py-1.5">Cabin</th>
                  <th className="text-left font-semibold px-3 py-1.5">Imported</th>
                  <th className="text-right font-semibold px-3 py-1.5">Deals</th>
                  <th className="text-left font-semibold px-3 py-1.5">By</th>
                  <th className="text-right font-semibold px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.id} className="border-t border-black/5">
                    <td className="px-3 py-1.5">{s.region_label}</td>
                    <td className="px-3 py-1.5 capitalize">{s.cabin}</td>
                    <td className="px-3 py-1.5">{fmtImportedAt(s.imported_at)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{s.trip_count}</td>
                    <td className="px-3 py-1.5 text-neutral-500">{s.imported_by}</td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {canManageSnapshots && (
                        <button onClick={() => deleteSnap(s.id)} className="btn btn-ghost text-[12px] text-red-700">Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
      )}

      <div className="mt-2">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <h3 className="font-display font-bold text-[15px]">Deals</h3>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
              placeholder="Origin (IATA or city)"
              className="input text-[12.5px] py-1.5 w-[180px]"
              data-testid="filter-origin"
            />
            <span className="text-[11px] text-neutral-400">→</span>
            <input
              type="text"
              value={destFilter}
              onChange={(e) => setDestFilter(e.target.value)}
              placeholder="Destination (IATA or city)"
              className="input text-[12.5px] py-1.5 w-[200px]"
              data-testid="filter-destination"
            />
            <div className="inline-flex rounded-[10px] border border-black/10 overflow-hidden text-[12.5px]" data-testid="deals-scope">
              {[
                { key: "international", label: "International" },
                { key: "domestic",      label: "Domestic" },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setScope(s.key)}
                  data-testid={`scope-${s.key}`}
                  className={`px-3 py-1.5 font-semibold transition-colors ${
                    scope === s.key ? "bg-black text-white" : "bg-white text-neutral-600 hover:text-black"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {tripsLoading && totalTrips === 0 ? (
          <div className="text-[12.5px] text-neutral-500">Loading…</div>
        ) : totalTrips === 0 ? (
          (originFilter || destFilter) ? (
            <div className="text-[12.5px] text-neutral-500">
              No {scope} routes match the selected origin/destination.
              <button
                type="button"
                onClick={() => { setOriginFilter(""); setDestFilter(""); }}
                className="ml-2 underline text-neutral-700 hover:text-black"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="text-[12.5px] text-neutral-500">
              No {scope} routes in any imported snapshot — click <b>Sync deals</b> above to import more.
            </div>
          )
        ) : (
          <div className="overflow-x-auto border border-black/5 rounded-[10px] bg-white">
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="text-left font-semibold px-3 py-1.5">Route</th>
                  <th className="text-left font-semibold px-3 py-1.5">When</th>
                  <th className="text-left font-semibold px-3 py-1.5">Cabin</th>
                  <th className="text-right font-semibold px-3 py-1.5">Miles</th>
                  <th className="text-right font-semibold px-3 py-1.5">PPH</th>
                  <th className="text-right font-semibold px-3 py-1.5">Taxes</th>
                  <th className="text-left font-semibold px-3 py-1.5">Flight</th>
                  <th className="text-right font-semibold px-3 py-1.5">Duration</th>
                  <th className="text-right font-semibold px-3 py-1.5">Seats</th>
                  <th className="text-left font-semibold px-3 py-1.5">Updated</th>
                  <th className="text-left font-semibold px-3 py-1.5">Book</th>
                  <th className="text-left font-semibold px-3 py-1.5">Share</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrips.map((t) => {
                  const offs = dayOffset(t.departs_at, t.arrives_at);
                  return (
                    <tr
                      key={t.id}
                      className={`border-t border-black/5 align-top ${t.is_stale ? "opacity-60 bg-neutral-50" : ""}`}
                      title={t.is_stale && t.stale_at ? `Marked stale ${new Date(t.stale_at).toLocaleString()}` : undefined}
                    >
                      <td className="px-3 py-1.5">
                        <div className="font-mono text-[12.5px] flex items-center gap-1.5">
                          <span>{airportLabel(t.origin, t.origin_city)} → {airportLabel(t.destination, t.destination_city)}</span>
                          {t.is_stale && (
                            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[9.5px] font-semibold tracking-wide uppercase px-1.5 py-0.5">
                              Stale
                            </span>
                          )}
                        </div>
                        {t.stops > 0 && (
                          <div className="text-[11px] text-neutral-500 mt-0.5">
                            {t.stops} stop{t.stops === 1 ? "" : "s"}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 tabular whitespace-nowrap">
                        <div>{localDate(t.departs_at)}</div>
                        <div className="text-[11px] text-neutral-500 mt-0.5">
                          {localTime(t.departs_at)} → {localTime(t.arrives_at)}{offs > 0 ? <span className="text-amber-700"> +{offs}</span> : null}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 capitalize">{t.cabin || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-semibold tabular">
                        <div>{(t.mileage_cost || 0).toLocaleString()}</div>
                        {typeof t.cpp === "number" && t.cpp > 0 && (
                          <div className="text-[10.5px] font-normal text-emerald-700 mt-0.5">
                            {t.cpp.toFixed(1)}¢/pt
                            {typeof t.cash_price === "number" && t.cash_price > 0 && (
                              <span className="text-neutral-400"> · ≈${Math.round(t.cash_price).toLocaleString()}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular text-neutral-500">{pph(t.mileage_cost, t.total_duration_min) ?? "—"}</td>
                      <td className="px-3 py-1.5 text-right tabular">{fmtTaxes(t.taxes_amount, t.taxes_currency) || "—"}</td>
                      <td className="px-3 py-1.5">
                        <div className="font-mono text-[12px]">{t.flight_numbers}</div>
                        {carrierNames(t.carriers) && (
                          <div className="text-[10.5px] text-neutral-500 mt-0.5">{carrierNames(t.carriers)}</div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular whitespace-nowrap">{fmtDuration(t.total_duration_min)}</td>
                      <td className="px-3 py-1.5 text-right">{t.remaining_seats ?? "—"}</td>
                      <td
                        className="px-3 py-1.5 whitespace-nowrap text-[11.5px] text-neutral-600"
                        title={`Discovered: ${fmtImportedAt(t.discovered_at)}\nUpdated: ${fmtImportedAt(t.deal_updated_at)}`}
                      >
                        <div>{relativeAge(t.deal_updated_at)}</div>
                        {t.discovered_at && t.deal_updated_at && t.discovered_at !== t.deal_updated_at && (
                          <div className="text-[10px] text-neutral-400 mt-0.5">
                            found {relativeAge(t.discovered_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5"><BookButton trip={t} /></td>
                      <td className="px-3 py-1.5"><ShareButtons trip={t} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-black/5 text-[11.5px] text-neutral-600 bg-neutral-50">
              <div>
                Showing <span className="tabular font-medium">{(page - 1) * PAGE_SIZE + 1}</span>
                –<span className="tabular font-medium">{Math.min(page * PAGE_SIZE, totalTrips)}</span>
                {" "}of <span className="tabular font-medium">{totalTrips.toLocaleString()}</span>
                {tripsLoading && <span className="ml-2 text-neutral-400">refreshing…</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={page <= 1 || tripsLoading}
                  className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
                >
                  « First
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || tripsLoading}
                  className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
                >
                  ‹ Prev
                </button>
                <span className="px-2 tabular">
                  Page {page} / {Math.max(1, totalPages)}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages || 1, p + 1))}
                  disabled={page >= totalPages || tripsLoading}
                  className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
                >
                  Next ›
                </button>
                <button
                  type="button"
                  onClick={() => setPage(totalPages || 1)}
                  disabled={page >= totalPages || tripsLoading}
                  className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40"
                >
                  Last »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
